import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useUserContext } from 'app/services/UserProvider/UserProvider'
import tagManager from 'app/tagManager'
import Content from 'components/content/Content/Content'
import Form from 'components/core/Form/Form'
import Layout from 'components/core/Layout/Layout'
import { validateEmail, validateString } from 'lib/util/validators'
import styles from './NewsletterSignUpForm.module.scss'

const CONTENT = [
  {
    type: 'standard',
    align: 'center',
    content:
      '<p>Stay in the loop with our latest news and alerts! Be the first to know about exciting developments & exclusive offers!</p>'
  }
]

const CONTENT_COMPLETE = [
  {
    type: 'standard',
    align: 'center',
    content:
      '<h1 style="color: var(--color-d-accent-a);">We\'re thrilled to have you on board!</h1><p>Get ready to stay up-to-date with the latest news and receive timely alerts delivered right to your inbox.</p>'
  }
]

/**
 * The `NewsletterSignUpForm`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function NewsletterSignUpForm (props) {
  const { account, signUpNewsletter } = useUserContext()
  const [isBusy, setIsBusy] = useState(false)
  const providedEmail = useMemo(
    () => account?.wallet?.authDetails?.email || '',
    []
  )
  const [isComplete, setIsComplete] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [email, setEmail] = useState(providedEmail)
  const firstFieldRef = useRef()

  /**
   * On submit
   */
  const onSubmit = useCallback(
    async formData => {
      if (isBusy) {
        // Is buys
        return
      }

      setIsBusy(true)

      const res = await signUpNewsletter(formData)

      setIsBusy(false)

      if (res.success) {
        // Completed
        setIsComplete(true)

        tagManager.trackEvent({
          event: 'signup-newsletter-success'
        })

        return
      }

      // Failed
      setErrorMessage(res.message)

      tagManager.trackEvent({
        event: 'signup-newsletter-error'
      })
    },
    [isBusy]
  )

  /**
   * Handle before submit
   */
  const handleBeforeSubmit = useCallback(() => {
    setErrorMessage('')
  }, [])

  /**
   * Handle submit
   */
  const handleSubmit = useCallback(
    (e, formData) => {
      onSubmit(formData)
    },
    [onSubmit]
  )

  /**
   * Handle change of email input
   */
  const handleChangeEmail = useCallback(e => {
    setEmail(e.target.value)
  }, [])

  /**
   * On mount
   */
  useEffect(() => {
    // Focus the first field
    if (!firstFieldRef.current) {
      return
    }

    firstFieldRef.current.focus()
  }, [])

  return (
    <div className={styles.newsletter}>
      {!isComplete && (
        <>
          <Layout.Body align="center">
            <Content items={CONTENT} />
          </Layout.Body>
          <Form
            isBusy={isBusy}
            onBeforeSubmit={handleBeforeSubmit}
            onSubmit={handleSubmit}
            errorMessage={errorMessage}
          >
            <Form.Group display="inline">
              <Form.InputField
                ref={firstFieldRef}
                label="First Name"
                name="firstName"
                validate={validateString}
              />
              <Form.InputField
                label="Last Name"
                name="lastName"
                validate={validateString}
              />
            </Form.Group>

            <Form.Group>
              <Form.InputField
                label="Email"
                name="email"
                type="email"
                value={email}
                validate={validateEmail}
                onChange={handleChangeEmail}
              />
            </Form.Group>

            <Form.Group>
              <Form.Submit size="inherit">Sign Up</Form.Submit>
            </Form.Group>
          </Form>
        </>
      )}
      {isComplete && (
        <>
          <Layout.Body align="center" color="c">
            <Content items={CONTENT_COMPLETE} />
          </Layout.Body>
        </>
      )}
    </div>
  )
}

// Memoize
export default memo(NewsletterSignUpForm)
